// /home/heliowallet/frontend/components/wallet/TokenList.tsx
'use client';

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useWallet } from '@/hooks/useWallet';
import { useBalance, useAccount } from 'wagmi';
import { useSelectedRpc } from '@/hooks/useSelectedRpc';
import { useRpcPatcher } from '@/hooks/useRpcPatcher';
import FallbackImage from '@/components/FallbackImage';
import {
  PlusIcon,
  TrashIcon,
  SparklesIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  DocumentDuplicateIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { CheckIcon as CheckIconSolid } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';

interface TokenListProps {
  onTokenSelect?: (token: any) => void;
  selectedToken?: any;
}

type SortBy = 'name' | 'balance' | 'value';

export default function TokenList({ onTokenSelect, selectedToken }: TokenListProps) {
  const { address } = useAccount();
  
  const {
    chainId,
    chainInfo,
    tokens,
    tokenPrices,
    nativeTokenPrice,
    isLoadingBalance,
    isLoadingTokens,
    addCustomToken,
    scanPopularTokens,
    removeCustomToken,
    reloadBalance
  } = useWallet();
  
  // Use selected RPC
  const { publicClient: customPublicClient, selectedRpc } = useSelectedRpc(chainId);
  
  // Patch wagmi transport when RPC changes
  useRpcPatcher(chainId, selectedRpc || undefined);
  
  // Use balance - Wagmi should use the selected RPC via useRpcPatcher
  const { data: balance, refetch: refetchBalance } = useBalance({ address });

  console.log(`💰 [TokenList] Selected RPC: ${selectedRpc}`);
  
  // Force refetch balance when chain changes
  useEffect(() => {
    if (address && chainId) {
      console.log(`🔄 [TokenList] Chain changed to ${chainId}, refetching balance...`);
      refetchBalance();
    }
  }, [chainId, address, refetchBalance]);
  
  const [tokenAddress, setTokenAddress] = useState('');
  const [isAddingToken, setIsAddingToken] = useState(false);
  const [isScanningPopular, setIsScanningPopular] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('value');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [expandedToken, setExpandedToken] = useState<string | null>(null);
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  
  // Create native token object
  const nativeToken = useMemo(() => {
    if (!chainInfo || balance === undefined) return null;
    
    return {
      address: 'native',
      symbol: chainInfo.nativeCurrency.symbol,
      name: chainInfo.nativeCurrency.name,
      decimals: chainInfo.nativeCurrency.decimals,
      balance: balance?.formatted || '0',
      logoURI: chainInfo.icon,
      isNative: true,
      chainId: chainInfo.chainId,
      hidden: false,
      isCustom: false,
      source: 'native' as const,
      addedAt: Date.now()
    };
  }, [chainInfo, balance]);
  
  // Calculate total portfolio value including native token
  const totalValue = useMemo(() => {
    let total = 0;
    
    // Add native token value
    if (nativeToken && nativeTokenPrice) {
      total += parseFloat(nativeToken.balance || '0') * nativeTokenPrice;
    }
    
    // Add token values
    total += tokens.reduce((sum, token) => {
      const price = tokenPrices[token.address.toLowerCase()];
      if (price && token.balance) {
        return sum + (parseFloat(token.balance) * price);
      }
      return sum;
    }, 0);
    
    return total;
  }, [tokens, tokenPrices, nativeToken, nativeTokenPrice]);
  
  // Combine native token with other tokens
  const displayTokens = useMemo(() => {
    const tokenList = [];
    
    // Always add native token first
    if (nativeToken) {
      tokenList.push(nativeToken);
    }
    
    // Add other tokens
    tokenList.push(...tokens);
    
    return tokenList;
  }, [nativeToken, tokens]);
  
  // Filter and sort tokens
  const processedTokens = useMemo(() => {
    let filtered = [...displayTokens];
    
    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(token =>
        token.symbol.toLowerCase().includes(query) ||
        token.name.toLowerCase().includes(query) ||
        (token.address !== 'native' && token.address.toLowerCase().includes(query))
      );
    }
    
    // Apply sort
    return filtered.sort((a, b) => {
      // Always keep native token at top
      if ('isNative' in a && a.isNative && !('isNative' in b && b.isNative)) return -1;
      if (!('isNative' in a && a.isNative) && 'isNative' in b && b.isNative) return 1;
      
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'balance':
          return parseFloat(b.balance || '0') - parseFloat(a.balance || '0');
        case 'value':
          const aPrice = ('isNative' in a && a.isNative) ? (nativeTokenPrice || 0) : (tokenPrices[a.address.toLowerCase()] || 0);
          const bPrice = ('isNative' in b && b.isNative) ? (nativeTokenPrice || 0) : (tokenPrices[b.address.toLowerCase()] || 0);
          const aValue = parseFloat(a.balance || '0') * aPrice;
          const bValue = parseFloat(b.balance || '0') * bPrice;
          return bValue - aValue;
        default:
          return 0;
      }
    });
  }, [displayTokens, tokenPrices, nativeTokenPrice, searchQuery, sortBy]);
  
  // Handle add custom token
  const handleAddToken = async () => {
    if (!tokenAddress.trim()) {
      toast.error('Please enter token address');
      return;
    }
    
    setIsAddingToken(true);
    try {
      // Add token and it will automatically fetch balance and show in list
      const addedToken = await addCustomToken(tokenAddress.trim());
      if (addedToken) {
        setTokenAddress('');
        toast.success(`${addedToken.symbol} added successfully`);
        
        // Auto-select the newly added token
        handleTokenClick(addedToken);
        
        // Emit event to notify other components
        window.dispatchEvent(new CustomEvent('token-added', {
          detail: { token: addedToken }
        }));
        
        // Reload balance immediately
        reloadBalance();
        
        // Reload all balances to ensure fresh data
        setTimeout(() => {
          reloadBalance();
        }, 1000);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to add token');
    } finally {
      setIsAddingToken(false);
    }
  };
  
  // Handle scan popular tokens
  const handleScanPopularTokens = async () => {
    setIsScanningPopular(true);
    try {
      const foundTokens = await scanPopularTokens();
      if (foundTokens.length > 0) {
        toast.success(`Found ${foundTokens.length} tokens with balance`);
        
        // Auto-select the first token found
        if (foundTokens.length > 0) {
          handleTokenClick(foundTokens[0]);
        }
        
        // Reload balances to show updated list
        setTimeout(() => {
          reloadBalance();
        }, 500);
      } else {
        toast('No tokens with balance found', {
          icon: '🔍',
        });
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to scan tokens');
    } finally {
      setIsScanningPopular(false);
    }
  };
  
  // Handle remove custom token
  const handleRemoveToken = useCallback((address: string, symbol: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (confirm(`Remove ${symbol} from your token list?`)) {
      removeCustomToken(address);
      toast.success(`${symbol} removed`);
      
      // Close expanded if it was this token
      if (expandedToken === address) {
        setExpandedToken(null);
      }
    }
  }, [removeCustomToken, expandedToken]);
  
  // Handle copy address
  const handleCopyAddress = useCallback((address: string, event: React.MouseEvent) => {
    event.stopPropagation();
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    toast.success('Address copied');
    setTimeout(() => setCopiedAddress(null), 2000);
  }, []);
  
  // Handle token selection
  const handleTokenClick = useCallback((token: any) => {
    if (onTokenSelect) {
      onTokenSelect(token);
    }
  }, [onTokenSelect]);
  
  // Handle expand/collapse
  const toggleExpanded = useCallback((address: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedToken(prev => prev === address ? null : address);
  }, []);
  
  // Format token value
  const formatTokenValue = (token: any) => {
    const price = ('isNative' in token && token.isNative) ? nativeTokenPrice : tokenPrices[token.address.toLowerCase()];
    if (!price || !token.balance) return null;
    
    const value = parseFloat(token.balance) * price;
    if (value < 0.01) return null;
    
    return value;
  };
  
  if (!chainId) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
        <p className="text-center text-gray-500 dark:text-gray-400">
          Connect wallet to view tokens
        </p>
      </div>
    );
  }
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Your Tokens
            </h3>
            {totalValue > 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Total: ${totalValue.toFixed(2)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={reloadBalance}
              disabled={isLoadingBalance}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh balances"
            >
              <ArrowPathIcon className={`h-4 w-4 ${isLoadingBalance ? 'animate-spin' : ''}`} />
            </button>
            {displayTokens.length > 0 && (
              <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
                {displayTokens.length}
              </span>
            )}
          </div>
        </div>
        
        {/* Search & Sort */}
        <div className="space-y-2">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, symbol, or address"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          
          {/* Sort Menu */}
          <div className="relative">
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm flex items-center gap-1"
            >
              <ChevronDownIcon className="h-3.5 w-3.5" />
              Sort: {sortBy === 'value' ? 'Value' : sortBy === 'balance' ? 'Balance' : 'Name'}
            </button>
            
            {showSortMenu && (
              <div className="absolute top-full mt-1 left-0 w-32 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                {(['value', 'balance', 'name'] as SortBy[]).map(option => (
                  <button
                    key={option}
                    onClick={() => {
                      setSortBy(option);
                      setShowSortMenu(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      sortBy === option ? 'bg-gray-50 dark:bg-gray-700' : ''
                    }`}
                  >
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Token List */}
      <div className="flex-1 overflow-hidden">
        {isLoadingTokens && displayTokens.length === 0 ? (
          <div className="p-8 text-center">
            <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500 dark:text-gray-400">Loading tokens...</p>
          </div>
        ) : processedTokens.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-700 overflow-y-auto max-h-[480px]">
            {processedTokens.map((token) => {
              const isExpanded = expandedToken === token.address;
              const tokenValue = formatTokenValue(token);
              const isNative = 'isNative' in token && token.isNative;
              
              const isSelected = selectedToken && (
                (('isNative' in selectedToken && selectedToken.isNative) && ('isNative' in token && token.isNative)) ||
                (selectedToken.address === token.address)
              );
              
              return (
                <div key={token.address} className="group">
                  <div
                    className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer flex items-center ${
                      isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500' : ''
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTokenClick(token);
                    }}
                  >
                    {/* Expand/Collapse Icon */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(token.address, e);
                      }}
                      className="mr-3 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                      )}
                    </button>
                    
                    {/* Token Info */}
                    <div className="flex items-center justify-between flex-1 min-w-0">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                        <FallbackImage
                          src={isNative ? chainInfo?.icon : token.logoURI}
                          alt={token.symbol}
                          fallback={isNative ? 'chain' : 'token'}
                          fallbackText={token.symbol}
                          className="h-8 w-8 rounded-full flex-shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                            <p className="font-medium text-gray-900 dark:text-white text-sm">
                              {token.symbol}
                            </p>
                            {isNative && (
                              <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded">
                                Native
                              </span>
                            )}
                            {token.isCustom && (
                              <span className="text-xs px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded">
                                Custom
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{token.name}</p>
                        </div>
                      </div>
                      <div className="text-right ml-2">
                        <p className="font-medium text-gray-900 dark:text-white text-xs sm:text-sm">
                          {parseFloat(token.balance || '0').toFixed(4)}
                        </p>
                        {tokenValue !== null && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            ${tokenValue.toFixed(2)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-4 pb-3 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700">
                      <div className="pt-2 space-y-2">
                        {!isNative && (
                          <div className="text-xs">
                            <p className="text-gray-500 dark:text-gray-400 mb-1">Contract Address</p>
                            <div className="flex items-center gap-2">
                              <p className="font-mono text-gray-700 dark:text-gray-300 break-all">
                                {token.address}
                              </p>
                              <button
                                onClick={(e) => handleCopyAddress(token.address, e)}
                                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                              >
                                {copiedAddress === token.address ? (
                                  <CheckIconSolid className="h-3.5 w-3.5 text-green-500" />
                                ) : (
                                  <DocumentDuplicateIcon className="h-3.5 w-3.5 text-gray-500" />
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {(isNative ? nativeTokenPrice : tokenPrices[token.address.toLowerCase()]) && (
                          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                            <CurrencyDollarIcon className="h-3 w-3" />
                            <span>
                              Price: ${
                                isNative
                                  ? nativeTokenPrice?.toFixed(4) 
                                  : tokenPrices[token.address.toLowerCase()].toFixed(4)
                              }
                            </span>
                          </div>
                        )}
                        
                        {/* Remove button for custom tokens only */}
                        {token.isCustom && !isNative && (
                          <div className="pt-2">
                            <button
                              onClick={(e) => handleRemoveToken(token.address, token.symbol, e)}
                              className="px-3 py-1.5 bg-red-100 dark:bg-red-900/20 hover:bg-red-200 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 rounded text-xs flex items-center gap-1"
                            >
                              <TrashIcon className="h-3.5 w-3.5" />
                              Remove
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
              <SparklesIcon className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-gray-500 dark:text-gray-400 font-medium">
              {searchQuery ? 'No tokens found' : 'No tokens in your wallet'}
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              {searchQuery ? 'Try a different search' : 'Add tokens or scan for popular ones'}
            </p>
          </div>
        )}
      </div>
      
      {/* Footer - Add Token */}
      <div className="p-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={tokenAddress}
            onChange={(e) => setTokenAddress(e.target.value)}
            placeholder="Token contract address"
            onKeyPress={(e) => e.key === 'Enter' && handleAddToken()}
            className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
          <div className="flex gap-2">
            <button
              onClick={handleAddToken}
              disabled={isAddingToken || !tokenAddress}
              className="flex-1 sm:flex-none px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 text-sm"
            >
              {isAddingToken ? (
                <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <PlusIcon className="h-3.5 w-3.5" />
              )}
              <span className="sm:inline">Add</span>
            </button>
            <button
              onClick={handleScanPopularTokens}
              disabled={isScanningPopular}
              className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center"
              title="Scan for popular tokens with balance"
            >
              {isScanningPopular ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              ) : (
                <SparklesIcon className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
        
        {/* Help text */}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          Add custom tokens or scan for popular ones with balance
        </p>
      </div>
    </div>
  );
}